import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiPaths } from "../../../service/apiPaths";
import ApiService from "../../../service/ApiService";
import {
  formatDateToISODateString,
  limit,
  updateComponent,
  ViewType,
} from "../../../util/constant";
import { IProject } from "./Project.props";

interface IProjectView {
  _id: string;
  name: string;
  description: string;
  status: string;
  totalHours: number;
  users: Array<{
    name: string;
    email: string;
    role: string;
    totalHours: number;
    dateWise: Array<{
      date: string;
      hours: number;
    }>;
  }>;
}

const useProject = () => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null
  );
  const [selectedProjectView, setSelectedProjectView] =
    useState<IProjectView | null>(null);
  const [projectData, setProjectData] = useState<IProject[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false); // State to manage the visibility of the confirmation dialog
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleDateFilter = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = ``;
      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint = `${apiPaths.getProjectWithStatus}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${page}&limit=${limit}`;
        if (search) {
          endpoint = `${apiPaths.getProjectWithStatus}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&search=${search}&page=${page}&limit=${limit}`;
        }
      } else {
        endpoint = `${apiPaths.getProjectWithStatus}?page=${page}&limit=${limit}`;
        if (search) {
          endpoint = `${apiPaths.getProjectWithStatus}?search=${search}&page=${page}&limit=${limit}`;
        }
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      const rows = result?.data ?? [];
      setProjectData(
        rows.map((row: IProject & { id?: string }) => ({
          ...row,
          _id: (row._id ?? row.id) as string,
        }))
      );
      setTotalPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmStatusUp = async () => {
    setLoading(true);
    const status = projectData.find(
      (project: any) => String(project._id ?? project.id) === String(deletingId)
    );
    const data = {
      status: status?.status.toString() === "Active" ? "InActive" : "Active",
    };
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateProject}${deletingId}`,
        data: data,
      });
      setProjectData((prevData) =>
        prevData.map((project) =>
          String(project._id ?? project.id) === String(deletingId)
            ? {
                ...project,
                status:
                  project?.status?.toString() === "Active"
                    ? "InActive"
                    : "Active",
              }
            : project
        )
      );
    } catch (error) {
      console.error("Error deleting project:", error);
    } finally {
      setLoading(false);
    }
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleEdit = (id: string) => {
    setSelectedProjectId(id);
  };

  const handleView = (project: IProjectView) => {
    setSelectedProjectView(project);
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };
  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addProject");
  };
  const updateScreen = (type: ViewType) => {
    updateComponent(type, setSelectedProjectView, setSelectedProjectId);
  };
  return {
    selectedProjectId,
    selectedProjectView,
    projectData,
    showConfirmation,
    handleAdd,
    handleEdit,
    handleView,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    updateScreen,
    loading,
    setSearch,
    search,
    setPage,
    page,
    totalPage,
  };
};

export default useProject;
